import { useState } from "react";

import {
  CategoryScale,
  ChartDataset,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { BilledOperationChartData } from "src/types/billedOperation";

import { useBilledOperationsQuery } from "@sso/apis/billedOperations";
import { SelectDataType } from "@sso/components/Form/MultiSelect";
import { Flex } from "@sso/ui/FlexBox";
import { Typography } from "@sso/ui/Typography";

import { BilledOperationsFilters } from "./billedOperationsFilters";
import { InfoBox } from "./infoBox";

export const BilledOperations = () => {
  const billableOperationDictionary: Record<string, string> = {
    geoLocation: "429B585C-ADCA-4108-8001-4ACB1988F29A",
    calculation: "09AD9CF3-B0ED-4198-8C69-664E29BFD5AA",
    rechargeRequest: "CF307FC8-DCC2-49A2-AAB6-31281F80CD55",
  };

  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  // Setup default dates to filter by
  let startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  startDate.setHours(0, 0, 0);
  let endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);
  endDate.setHours(0, 0, 0);
  let defaultBillableFilter: string[] = [];

  const [filterStartDate, setFilterStartDate] = useState(startDate);
  const [filterEndDate, setFilterEndDate] = useState(endDate);
  const [filterBillableOperations, setFilterBillableOperations] = useState(defaultBillableFilter);
  const [filteredUserAccounts, setfilteredUserAccounts] = useState<string[]>([]);
  const [filteredApiAccounts, setfilteredApiAccounts] = useState<string[]>([]);
  const [organisationId, setOrganisationId] = useState<number | null>(null);

  // Creates the query string paramaters for the get request
  const getBilledOperationsQuery = () => {
    let result = "startDate=" + filterStartDate.toISOString() + "&endDate=" + filterEndDate.toISOString();

    if (filterBillableOperations.length > 0) {
      for (let i = 0; i < filterBillableOperations.length; i++) {
        result += "&";

        result += "billableOperationFilters[" + i + "]=" + filterBillableOperations[i];
      }
    }

    if (filteredUserAccounts.length > 0) {
      for (let i = 0; i < filteredUserAccounts.length; i++) {
        result += "&";
        result += "userFilters[" + i + "]=" + filteredUserAccounts[i];
      }
    }

    if (filteredApiAccounts.length > 0) {
      for (let i = 0; i < filteredApiAccounts.length; i++) {
        result += "&";

        result += "apiAccountFilters[" + i + "]=" + filteredApiAccounts[i];
      }
    }

    if (organisationId != null) {
      result += "&organisationId=" + organisationId;
    }

    return result;
  };

  const { data, error, isLoading: loading } = useBilledOperationsQuery(getBilledOperationsQuery());

  const handleStartDateChange = (event: any) => {
    let date = new Date(event?.target.value);
    date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    setFilterStartDate(date);
  };

  const handleEndDateChange = (event: any) => {
    let date = new Date(event?.target.value);
    date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    setFilterEndDate(date);
  };

  const handleBillableOperationFilterCheckChange = (event: any) => {
    var checkName = event?.target.name;
    var checkBillableKey = billableOperationDictionary[checkName];
    var billableOps = [...filterBillableOperations];

    if (event?.target.checked) {
      billableOps.push(checkBillableKey);
    } else {
      billableOps = billableOps.filter(x => x !== checkBillableKey);
    }

    setFilterBillableOperations(billableOps);
  };

  const handleIsChecked = (operationKey: string): boolean => {
    var operation = billableOperationDictionary[operationKey];
    let result = filterBillableOperations.filter(x => x === operation).length > 0;
    return result;
  };

  const handleApiFilterChange = (organisationId: number | null) => {
    setOrganisationId(organisationId);
  };

  const handleUserFiltersChanged = (selectedUsers: SelectDataType[], selectedApiAccounts: SelectDataType[]) => {
    if (selectedUsers.length === 0) {
      setfilteredUserAccounts([]);
    }
    else {
      setfilteredUserAccounts(selectedUsers.map(x => x.key.toString()));
    }

    if (selectedApiAccounts.length === 0) {
      setfilteredApiAccounts([]);
    }
    else {
      setfilteredApiAccounts(selectedApiAccounts.map(x => x.key.toString()));
    }
  };

  // Calculate the chart data
  let totalsChartData: BilledOperationChartData[] = [];
  let chartData: Record<string, BilledOperationChartData[]> = {};

  for (const key in billableOperationDictionary) {
    // Each billable operation has its own array of chart data
    chartData[key] = [] as BilledOperationChartData[];
  }

  // Calculates the data for the chart values
  let loop = new Date(filterStartDate);
  while (loop <= new Date(filterEndDate)) {
    const currentDate = loop;
    totalsChartData.push({
      date: loop.toISOString().split("T")[0],
      operations: data?.billedOperations.filter(
        x => new Date(x.createDate).toISOString().split("T")[0] === currentDate.toISOString().split("T")[0],
      ),
      count: 0,
      cost: 0,
    });

    for (const key in billableOperationDictionary) {
      chartData[key].push({
        date: loop.toISOString().split("T")[0],
        operations: data?.billedOperations
          .filter(x => x.billableOperationId.toUpperCase() === billableOperationDictionary[key])
          .filter(x => new Date(x.createDate).toISOString().split("T")[0] === currentDate.toISOString().split("T")[0]),
        count: 0,
        cost: 0,
      });
    }

    // Move to the next date
    var newDate = loop.setDate(loop.getDate() + 1);
    loop = new Date(newDate);
  }

  // Calculate the total cost and count for each day
  totalsChartData.forEach(x =>
    x.operations?.forEach(o => {
      x.cost += o.cost * o.multiplier;
      x.count += o.multiplier;
    }),
  );

  for (const key in billableOperationDictionary) {
    chartData[key].forEach(x =>
      x.operations?.forEach(o => {
        x.cost += o.cost * o.multiplier;
        x.count += o.multiplier;
      }),
    );
  }

  const getBillableOperationLineColour = (key: string): string => {
    switch (key) {
      case "geoLocation":
        return "#7ee356";
      case "calculation":
        return "#18a8f3";
      case "rechargeRequest":
        return "#FFD700";
    }

    return "#999999";
  };

  const getBillableOperationLabel = (key: string): string => {
    switch (key) {
      case "geoLocation":
        return "Geolocation Lookup";
      case "calculation":
        return "EVR Calculation";
      case "rechargeRequest":
        return "EVR Recharge Calculation";
    }

    return "";
  };

  const costDatasets: ChartDataset<"line", any>[] = [];
  const countDatasets: ChartDataset<"line", any>[] = [];
  for (const key in billableOperationDictionary) {
    if (
      filterBillableOperations.filter(x => x.toUpperCase() === billableOperationDictionary[key]).length > 0 ||
      filterBillableOperations.length === 0
    ) {
      const keyColour = getBillableOperationLineColour(key);
      const keyLabel = getBillableOperationLabel(key);

      costDatasets.push({
        data: chartData[key].map(x => x.cost),
        label: keyLabel,
        borderColor: keyColour,
        backgroundColor: keyColour,
      });
      countDatasets.push({
        data: chartData[key].map(x => x.count),
        label: keyLabel,
        borderColor: keyColour,
        backgroundColor: keyColour,
      });
    }
  }

  let totalCost = 0;
  data?.billedOperations.forEach(x => (totalCost += x.cost * x.multiplier));

  let totalActions = 0;
  data?.billedOperations.forEach(x => (totalActions += x.multiplier));

  if (loading) {
    return (
      <>
        <Typography as="h2" style={{ margin: 10 }}>
          Loading Billing
        </Typography>
      </>
    );
  } else if (error) {
    return (
      <>
        <Typography as="h2" style={{ margin: 10 }}>
          Error Loading Billing
        </Typography>
      </>
    );
  } else {
    return (
      <>
        <Typography as="h2" style={{ margin: 10, width: "100%" }}>
          Billing for {data?.organisation.name}
        </Typography>
        <div style={{ margin: 10, overflowY: "scroll", paddingRight: 10, width: "100%" }}>
          <BilledOperationsFilters
            handleBillableOperationFilterCheckChange={handleBillableOperationFilterCheckChange}
            handleEndDateChange={handleEndDateChange}
            handleIsChecked={handleIsChecked}
            handleStartDateChange={handleStartDateChange}
            filterEndDate={filterEndDate}
            filterStartDate={filterStartDate}
            handleUserFiltersChange={handleUserFiltersChanged}
            handleOrganisationFilterChange={handleApiFilterChange}
          />
          <Flex style={{ marginTop: 20 }}>
            <InfoBox title="Total Cost" body={"£" + totalCost.toFixed(2)}></InfoBox>
            <InfoBox title="Total Operations" body={totalActions}></InfoBox>
          </Flex>

          <Flex style={{ flexWrap: "wrap" }}>
            <div style={{ height: "450px", width: "100%" }}>
              <Line
                style={{ marginBottom: 15 }}
                data={{
                  labels: totalsChartData.map(x => x.date),
                  datasets: costDatasets,
                }}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      ticks: {
                        callback: function (value, _index, _ticks) {
                          return "£" + value;
                        },
                      },
                      min: 0,
                    },
                  },
                  plugins: { title: { display: true, text: "Cost by Type Breakdown", font: { size: 24 } } },
                }}
              />
            </div>
            <div style={{ height: "450px", width: "100%" }}>
              <Line
                style={{}}
                data={{ labels: totalsChartData.map(x => x.date), datasets: countDatasets }}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      min: 0,
                      ticks: {
                        stepSize: 1,
                      },
                    },
                  },
                  plugins: { title: { display: true, text: "Total Operations by Type Breakdown", font: { size: 24 } } },
                }}
              />
            </div>
          </Flex>
        </div>
      </>
    );
  }
};
